import type { FormApi } from 'final-form';
import React, { useCallback } from 'react';
import type { FormRenderProps } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { styles as sharedStyles } from '@swf/account';
import ButtonGroup from '@swf/components/ButtonGroup/ButtonGroup';
import createForm from '@swf/components/Forms/Form';
import { RawToggle } from '@swf/components/Inputs/Toggle';
import RegulatoryCheckboxes, {
  getEulaTypes
} from '@swf/components/RegulatoryCheckboxes/RegulatoryCheckboxes';
import Button from '@swf/components/StarsButton/StarsButton';
import StarsInfo from '@swf/components/StarsInfo/StarsInfo';
import Translation from '@swf/components/Translation/Translation';
import { FeatureFlag, Site } from '@swf/core/enums';
import useFeature from '@swf/core/hooks/useFeature';
import type { RegisterPayload } from '@swf/core/models/auth/register';
import type { Dispatch, RootState } from '@swf/core/store';
import { select } from '@swf/core/store';

import { RegistrationStyles } from '../../StarsAuth';
import AccountSecurityInfo from '../AccountSecurityInfo/AccountSecurityInfo';

import {
  TXTCLI_Continue,
  TXTCLI_Create_Account,
  TXTCLI_For_the_purposes_of_responsibl_ELL,
  TXTCLI_If_you_are_interested_in_imple_ELL,
  TXTCLI_Select_All,
  TXTWCL_Nearly_doneX
} from './RegistrationEula.messages.json';

interface FormValues {
  marketingMailList: boolean;
  eula?: boolean;
  privacy?: boolean;
  combinedEulaPrivacy?: boolean;
  gamingAccAgreement?: boolean;
}

const toggleAll = (e: React.ChangeEvent<HTMLInputElement>, formApi: FormApi<FormValues>) => {
  const valueKeys = Object.keys(formApi.getState().values);
  valueKeys.forEach((item) => {
    formApi.change(item as keyof FormValues, e.target.checked);
  });
};

const isToggleAllChecked = (eulaTypes: FormValues) =>
  Object.values(eulaTypes).every((el) => el === true);

const getInitialValues = (siteId: Site, separatedEulaPrivacyPolicy: boolean) => ({
  marketingMailList: false,
  ...getEulaTypes(siteId, separatedEulaPrivacyPolicy).reduce(
    (result, el) => ({ ...result, [el]: false }),
    {}
  )
});

const Form = createForm<FormValues>();

const RegistrationEula: React.FC = () => {
  const { state: locationState } = useLocation<Omit<RegisterPayload, 'marketingMailList'>>();

  const siteId = useSelector(select.site.getId);
  const verificationRequired = useSelector(select.account.isAgeVerificationRequired);
  const loading = useSelector((rootState: RootState) => rootState.loading.models.auth);

  const disableNewUkAge = useFeature(FeatureFlag.DisableNewUkAgeVerificationFlow);
  const separatedEulaPrivacyPolicy = useFeature(FeatureFlag.SeparatedEulaPrivacyPolicy);

  const {
    auth: { register }
  } = useDispatch<Dispatch>();

  const handleSubmit = useCallback(
    ({ marketingMailList = false }) => register({ marketingMailList, ...locationState }),
    [register, locationState]
  );

  return (
    <RegistrationStyles.Consumer>
      {(registrationStyles) => (
        <Form
          onSubmit={handleSubmit}
          initialValues={getInitialValues(siteId, separatedEulaPrivacyPolicy)}
          render={({ form }: FormRenderProps<FormValues>) => {
            const { invalid, validating, values } = form.getState();

            return (
              <>
                <h2 className={registrationStyles.title}>
                  <Translation {...TXTWCL_Nearly_doneX} />
                </h2>
                <RawToggle
                  onOffToggle
                  id="toggleAll"
                  name="toggleAll"
                  type="switch"
                  onChange={(e) => toggleAll(e, form)}
                  checked={isToggleAllChecked(values)}
                  label={<Translation {...TXTCLI_Select_All} />}
                />
                <RegulatoryCheckboxes variant="stars" />
                {siteId === Site.USPA && <AccountSecurityInfo />}
                <ButtonGroup>
                  <Button
                    block
                    type="submit"
                    disabled={invalid}
                    loading={loading || validating}
                    translation={
                      !disableNewUkAge && verificationRequired
                        ? TXTCLI_Continue
                        : TXTCLI_Create_Account
                    }
                  />
                </ButtonGroup>
                {siteId === Site.USNJ && (
                  <div className={sharedStyles.message}>
                    <StarsInfo>
                      <Translation {...TXTCLI_For_the_purposes_of_responsibl_ELL} />{' '}
                      <Translation {...TXTCLI_If_you_are_interested_in_imple_ELL} />
                    </StarsInfo>
                  </div>
                )}
              </>
            );
          }}
        />
      )}
    </RegistrationStyles.Consumer>
  );
};

export default RegistrationEula;
